/** Primary UI face — Google Sans Flex Variable (OFL-1.1, self-hosted). */
export const GOOGLE_SANS_FLEX_FONT_FAMILY = '"Google Sans Flex Variable"';

export const typography = {
  fontFamily: `${GOOGLE_SANS_FLEX_FONT_FAMILY}, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
  size: {
    xs: '0.75rem',
    sm: '0.8125rem',
    md: '0.875rem',
    lg: '1rem',
    xl: '1.25rem',
    '2xl': '1.5rem',
    '3xl': '1.75rem',
    '4xl': '2rem',
    '5xl': '2.5rem',
    '6xl': '3.5rem',
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
  },
  /**
   * Fluid display sizes. `cqi` is 1% of the login hero content width
   * (`container-type: inline-size` on the hero copy).
   */
  fluid: {
    heroHeadline: 'clamp(2rem, 8cqi, 5.5rem)',
    heroBrand: 'clamp(1.5rem, 4.2cqi, 2.75rem)',
    /** Supporting line under hero headline (~1/4–1/5 of display size, Notion-style). */
    heroTagline: 'clamp(1.25rem, 4.2cqi, 1.875rem)',
  },
} as const;

/** MUI spacing multipliers (theme.spacing(n)); base unit = 8px */
export const spacing = {
  half: 0.5,
  oneAndHalf: 1.5,
} as const;

export const elevation = {
  menu: 2,
} as const;

/**
 * Border radius tiers (MUI-native).
 * - `base`: px — ONLY for `theme.shape.borderRadius`
 * - `sm` / `md`: sx multipliers (× shape.borderRadius)
 * - `full`: pill override — ONLY in wrapper sx when needed
 */
export const radius = {
  base: 4,
  sm: 1,
  md: 2,
  full: 9999,
} as const;

export const form = {
  controlHeight: 36,
  /** Shorter field for toolbars. SearchField uses it; Autocomplete and Select opt in. */
  controlHeightCompact: 32,
  fontSize: typography.size.md,
  inputPaddingY: '6px',
  inputPaddingX: '14px',
  menuItemMinHeight: 32,
  menuItemPaddingY: '4px',
  menuItemPaddingX: spacing.oneAndHalf,
  menuMaxHeight: 320,
  labelFontSize: typography.size.md,
  menuPaperMarginTop: spacing.half,
  menuPaperElevation: elevation.menu,
} as const;

export const table = {
  headerFontSize: typography.size.md,
  bodyFontSize: typography.size.md,
  headerFontWeight: typography.weight.semibold,
  headerLetterSpacing: '0.02em',
  headerLineHeight: 1.5,
  headerPaddingY: '0px',
  headerPaddingX: '16px',
  headerRowHeight: 40,
  bodyPaddingY: '8px',
  bodyPaddingX: '8px',
  /** Left padding on the first body cell (table outer edge). */
  bodyPaddingXFirst: '16px',
  footerPaddingY: '0px',
  footerPaddingX: '8px',
  footerMinHeight: 36,
  paginationMinHeight: 36,
  paginationFontSize: typography.size.md,
  paginationIconPadding: '4px',
} as const;

export const button = {
  fontWeight: typography.weight.medium,
  /** `size="tiny"`. */
  heightTiny: 24,
  /** Horizontal padding for `size="tiny"` — MUI has no built-in tiny metrics. */
  paddingXTiny: 8,
  /** Icon glyph inside `size="tiny"`. */
  iconSizeTiny: 16,
  /** `size="small"`. */
  heightSmall: 28,
  /** `size="medium"` and the theme root default. */
  height: 32,
  /** `size="large"` — login, empty states, clock actions. */
  heightLarge: 40,
  lineHeight: '20px',
} as const;

export const layout = {
  appBarHeight: 64,
  drawerWidth: 220,
  /** Icon-only width when the nav column is collapsed. */
  navCollapsedWidth: 56,
  /** Default CSS width used when org viewport.minWidth is unset. */
  minAppWidth: 1280,
  /** List-page left filter panel width. */
  filterPanelWidth: 280,
} as const;

/**
 * Meta Business Suite–style soft ambient page background (light mode only).
 * Pastel radial washes over white — content cards stay on `background.paper`.
 */
export const surfaces = {
  appBackgroundColor: '#ffffff',
  appBackgroundImage: [
    'radial-gradient(103.89% 81.75% at 95.41% 106.34%, #eaf8ef 6%, rgba(234, 248, 239, 0) 79.68%)',
    'radial-gradient(297.85% 151.83% at -21.39% 8.81%, #faf1f1 0%, #faf1f1 15.29%, #f3edf5 21.39%, #e5f0fa 40.79%)',
  ].join(', '),
} as const;
